import { Injectable } from '@angular/core';
import { 
  HttpInterceptor, 
  HttpRequest, 
  HttpHandler, 
  HttpErrorResponse,
  HttpResponse
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { Router } from '@angular/router';

@Injectable()
export class MaintenanceInterceptor implements HttpInterceptor {
  private isOnMaintenancePage = false;

  constructor(private router: Router) {
    // Check current route
    this.router.events.subscribe(() => {
      this.isOnMaintenancePage = this.router.url.includes('/maintenance');
    });
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<any> {
    // Skip interceptor logic for maintenance page requests
    if (req.headers.get('Skip-Maintenance-Redirect') === 'true') {
      return next.handle(req);
    }

    return next.handle(req).pipe(
      map((event: any) => {
        // Check response for maintenance status
        if (event instanceof HttpResponse) {
          const body = event.body;
          if (body && body.maintenance === true && !this.isOnMaintenancePage) {
            this.router.navigate(['/maintenance'], {
              queryParams: { 
                message: body.message,
                retryAfter: body.retryAfter,
                reason: 'maintenance_mode'
              }
            });
          }
        }
        return event;
      }),
      catchError((error: HttpErrorResponse) => {
        // Handle HTTP errors that indicate backend is down
        if (this.isMaintenanceScenario(error) && !this.isOnMaintenancePage) {
          this.router.navigate(['/maintenance'], {
            queryParams: { 
              message: error.error?.message || 'Service temporarily unavailable. We are working to restore service.',
              retryAfter: error.error?.retryAfter || 300,
              reason: this.getErrorReason(error)
            }
          });
          return throwError(() => error);
        }
        return throwError(() => error);
      })
    );
  }

  private isMaintenanceScenario(error: HttpErrorResponse): boolean {
    // 503 Service Unavailable
    if (error.status === 503) {
      return true;
    }

    // Network errors (backend completely down)
    if (error.status === 0 || !error.status) {
      // Check if it's likely a backend failure vs network issue
      return error.message?.includes('Unknown Error') || 
             error.message?.includes('Http failure') ||
             error.message?.includes('ERR_CONNECTION_REFUSED') ||
             error.message?.includes('ERR_NETWORK');
    }

    // Database connection errors from backend
    if (error.status === 500) {
      const body = error.error;
      if (body && (
        body.message?.toLowerCase().includes('database') ||
        body.message?.toLowerCase().includes('connection') ||
        body.message?.toLowerCase().includes('maintenance') ||
        body.maintenance === true
      )) {
        return true;
      }
    }

    // Gateway timeout
    if (error.status === 504) {
      return true;
    }

    // Bad Gateway
    if (error.status === 502) {
      return true;
    }

    return false;
  }

  private getErrorReason(error: HttpErrorResponse): string {
    if (error.status === 503) return 'service_unavailable';
    if (error.status === 502) return 'bad_gateway';
    if (error.status === 504) return 'gateway_timeout';
    if (error.status === 0) return 'backend_unreachable';
    if (error.status === 500 && error.error?.maintenance) return 'maintenance_mode';
    return 'unknown_error';
  }
}

